import type {
  Contribution,
  DatedCashflow,
  DatedFinanceInput,
  DatedFinanceResult,
  DatedLedgerEntry,
  DatedMilestone,
  DatedMonthlyRollup,
  Facility,
  FinanceIssue,
  FinanceIssueCode,
} from "./dated-finance-types";

const DAY = 86_400_000;
const cent = (n: number): number =>
  Math.round((n + Number.EPSILON) * 100) / 100;
const object = (x: unknown): x is Record<string, unknown> =>
  typeof x === "object" && x !== null;
const iso = (d: Date): string => d.toISOString().slice(0, 10);
function date(value: unknown): Date | null {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value))
    return null;
  const result = new Date(`${value}T00:00:00.000Z`);
  return Number.isNaN(result.getTime()) || iso(result) !== value
    ? null
    : result;
}
const plusDays = (d: Date, n: number): Date => new Date(d.getTime() + n * DAY);
const endOfMonth = (d: Date): Date =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
const isMonthEnd = (d: Date): boolean =>
  d.getUTCDate() === endOfMonth(d).getUTCDate();
function nextOccurrence(
  d: Date,
  frequency: "daily" | "monthly" | "annual",
): Date {
  if (frequency === "daily") return plusDays(d, 1);
  const target = new Date(
    Date.UTC(
      d.getUTCFullYear() + (frequency === "annual" ? 1 : 0),
      d.getUTCMonth() + (frequency === "monthly" ? 1 : 0),
      1,
    ),
  );
  target.setUTCDate(Math.min(d.getUTCDate(), endOfMonth(target).getUTCDate()));
  return target;
}
function blank(dateValue: string): DatedCashflow {
  return {
    date: dateValue,
    project: 0,
    equity: 0,
    operator: 0,
    loanDraws: 0,
    loanRepayments: 0,
    interest: 0,
    paidInterest: 0,
    capitalisedInterest: 0,
    fees: 0,
    capitalisedDraws: 0,
    refinanceDraws: 0,
    refinanceRepayments: 0,
    closingDebt: 0,
    fundingShortfall: 0,
  };
}
interface Event {
  flow: DatedCashflow;
  categories: Set<string>;
  categoryAmounts: Map<string, number>;
}
interface FacilityState {
  f: Facility;
  start: Date;
  availabilityEnd: Date;
  maturity: Date;
  debt: number;
  interestAccrued: number;
  feeAccrued: number;
}

function signs(values: Array<{ value: number }>): number {
  let prior = 0;
  let changes = 0;
  for (const { value } of values) {
    const sign = Math.sign(value);
    if (sign && prior && sign !== prior) changes += 1;
    if (sign) prior = sign;
  }
  return changes;
}
function xnpv(
  values: Array<{ date: Date; value: number }>,
  rate: number,
  valuationDate = values[0]?.date,
): number | null {
  if (!values.length || !valuationDate) return null;
  const origin = valuationDate.getTime();
  return cent(
    values.reduce(
      (sum, v) =>
        sum +
        v.value / Math.pow(1 + rate, (v.date.getTime() - origin) / DAY / 365),
      0,
    ),
  );
}
function xirr(values: Array<{ date: Date; value: number }>): number | null {
  if (signs(values) !== 1) return null;
  const fn = (rate: number) => xnpv(values, rate) ?? Number.NaN;
  let left = -0.999999;
  let right = 1;
  let leftValue = fn(left);
  let rightValue = fn(right);
  while (leftValue * rightValue > 0 && right < 1_000_000) {
    right *= 2;
    rightValue = fn(right);
  }
  if (
    !Number.isFinite(leftValue) ||
    !Number.isFinite(rightValue) ||
    leftValue * rightValue > 0
  )
    return null;
  for (let i = 0; i < 160; i += 1) {
    const middle = (left + right) / 2;
    const middleValue = fn(middle);
    if (Math.abs(middleValue) < 0.000001) return middle;
    if (leftValue * middleValue <= 0) right = middle;
    else {
      left = middle;
      leftValue = middleValue;
    }
  }
  return (left + right) / 2;
}

/**
 * Strict UTC dated ledger. Project cash deliberately excludes draws, repayments, interest and fees.
 * Equity is the residual cash available to or required from the developer; operator flows are separate.
 */
export function computeDatedFinance(
  input: DatedFinanceInput,
): DatedFinanceResult {
  const issues: FinanceIssue[] = [];
  const unresolved: string[] = [];
  const fail = (
    code: FinanceIssueCode,
    message: string,
    extra: { date?: string; itemId?: string; unknown?: boolean } = {},
  ): void => {
    issues.push({
      code,
      message,
      ...(extra.date ? { date: extra.date } : {}),
      ...(extra.itemId ? { itemId: extra.itemId } : {}),
    });
    if (extra.unknown) unresolved.push(message);
  };
  const raw: Record<string, unknown> = object(input) ? input : {};
  const start = date(raw.startDate);
  const finish = date(raw.endDate);
  if (!start)
    fail("invalid-date", "startDate must be a valid YYYY-MM-DD UTC date", {
      date: String(raw.startDate),
      unknown: true,
    });
  if (!finish)
    fail("invalid-date", "endDate must be a valid YYYY-MM-DD UTC date", {
      date: String(raw.endDate),
      unknown: true,
    });
  if (start && finish && start > finish)
    fail("invalid-range", "startDate must not be after endDate", {
      unknown: true,
    });
  const inRange = (d: Date): boolean =>
    Boolean(start && finish && d >= start && d <= finish);
  const discountRate =
    typeof raw.discountRate === "number" &&
    Number.isFinite(raw.discountRate) &&
    raw.discountRate > -1
      ? raw.discountRate
      : 0.1;
  if (
    raw.discountRate !== undefined &&
    discountRate === 0.1 &&
    raw.discountRate !== 0.1
  )
    fail(
      "invalid-amount",
      "discountRate must be finite and greater than -100%",
      { unknown: true },
    );
  if (
    raw.unresolvedInputs !== undefined &&
    !Array.isArray(raw.unresolvedInputs)
  )
    fail(
      "missing-data",
      "unresolvedInputs must be an array of non-empty descriptions",
      { unknown: true },
    );
  for (const [index, value] of (Array.isArray(raw.unresolvedInputs)
    ? raw.unresolvedInputs
    : []
  ).entries()) {
    if (typeof value !== "string" || !value.trim())
      fail(
        "missing-data",
        `unresolvedInputs entry ${index + 1} must be a non-empty description`,
        { unknown: true },
      );
    else fail("missing-data", value.trim(), { unknown: true });
  }

  const milestoneDates = new Map<string, Date>();
  const pending = new Map<string, DatedMilestone>();
  if (raw.milestones !== undefined && !Array.isArray(raw.milestones))
    fail("missing-data", "milestones must be an array", { unknown: true });
  for (const value of Array.isArray(raw.milestones) ? raw.milestones : []) {
    if (!object(value) || typeof value.id !== "string" || !value.id) {
      fail("missing-data", "each milestone needs a non-empty id", {
        unknown: true,
      });
      continue;
    }
    if (pending.has(value.id)) {
      fail("missing-data", `milestone ${value.id} is duplicated`, {
        itemId: value.id,
        unknown: true,
      });
      continue;
    }
    pending.set(value.id, value as unknown as DatedMilestone);
  }
  let progress = true;
  while (pending.size && progress) {
    progress = false;
    for (const [id, milestone] of [...pending]) {
      const fixed = milestone.date === undefined ? null : date(milestone.date);
      if (milestone.date !== undefined && !fixed) {
        fail("invalid-date", `milestone ${id} has an invalid fixed date`, {
          itemId: id,
          unknown: true,
        });
        pending.delete(id);
        progress = true;
        continue;
      }
      const predecessors = Array.isArray(milestone.predecessors)
        ? milestone.predecessors
        : [];
      const missing = predecessors.find(
        (p) => !pending.has(p.id) && !milestoneDates.has(p.id),
      );
      if (missing) {
        fail(
          "missing-data",
          `milestone ${id} references missing predecessor ${missing.id}`,
          { itemId: id, unknown: true },
        );
        pending.delete(id);
        progress = true;
        continue;
      }
      if (predecessors.some((p) => !milestoneDates.has(p.id))) continue;
      const derived = predecessors.length
        ? new Date(
            Math.max(
              ...predecessors.map((p) => {
                if (
                  p.days !== undefined &&
                  (!Number.isInteger(p.days) || p.days < 0)
                )
                  fail(
                    "invalid-range",
                    `milestone ${id} has an invalid predecessor offset`,
                    { itemId: id, unknown: true },
                  );
                return plusDays(
                  milestoneDates.get(p.id) as Date,
                  Math.max(0, Math.trunc(p.days ?? 0)),
                ).getTime();
              }),
            ),
          )
        : null;
      if (!fixed && !derived) {
        fail("missing-data", `milestone ${id} needs a date or predecessor`, {
          itemId: id,
          unknown: true,
        });
        pending.delete(id);
        progress = true;
        continue;
      }
      if (fixed && derived && fixed < derived)
        fail(
          "milestone-conflict",
          `milestone ${id} fixed date precedes its dependencies`,
          { itemId: id, unknown: true },
        );
      const resolved = fixed ?? (derived as Date);
      if (!inRange(resolved))
        fail(
          "invalid-range",
          `milestone ${id} is outside the analysis period`,
          { itemId: id, unknown: true },
        );
      milestoneDates.set(id, resolved);
      pending.delete(id);
      progress = true;
    }
  }
  for (const id of pending.keys())
    fail(
      "milestone-cycle",
      `milestone ${id} is in an unresolved predecessor cycle`,
      { itemId: id, unknown: true },
    );

  const events = new Map<string, Event>();
  const ledger: DatedLedgerEntry[] = [];
  const costBuckets: Record<string, number> = {};
  const terminalDates = new Set<string>();
  const event = (d: string): Event => {
    const present = events.get(d);
    if (present) return present;
    const created = {
      flow: blank(d),
      categories: new Set<string>(),
      categoryAmounts: new Map<string, number>(),
    };
    events.set(d, created);
    return created;
  };
  const post = (
    when: unknown,
    line: Omit<DatedLedgerEntry, "date">,
    options: { funding?: string; bucket?: string } = {},
  ): void => {
    const d = date(when);
    if (!d) {
      fail("invalid-date", `${line.description} has an invalid date`, {
        date: String(when),
        unknown: true,
      });
      return;
    }
    if (!inRange(d)) {
      fail(
        "invalid-range",
        `${line.description} is outside the analysis period`,
        { date: iso(d), unknown: true },
      );
      return;
    }
    const e = event(iso(d));
    e.flow.project = cent(e.flow.project + line.project);
    e.flow.operator = cent(e.flow.operator + line.operator);
    if (line.project < 0 && options.funding) {
      e.categories.add(options.funding);
      e.categoryAmounts.set(
        options.funding,
        cent((e.categoryAmounts.get(options.funding) ?? 0) - line.project),
      );
    }
    if (line.project < 0 && options.bucket)
      costBuckets[options.bucket] = cent(
        (costBuckets[options.bucket] ?? 0) - line.project,
      );
    ledger.push({ date: iso(d), ...line });
  };
  const resolve = (value: unknown, label: string): string | null => {
    const direct = date(value);
    if (direct) return iso(direct);
    if (typeof value === "string" && milestoneDates.has(value))
      return iso(milestoneDates.get(value) as Date);
    fail(
      "missing-data",
      `${label} references an unknown milestone or invalid date ${String(value)}`,
      { unknown: true },
    );
    return null;
  };
  const postCost = (
    when: string,
    amount: number,
    category: string,
    id: string,
  ): void =>
    post(
      when,
      {
        category: "project-cost",
        amount: -amount,
        project: -amount,
        operator: 0,
        itemId: id,
        description: category,
      },
      { funding: category, bucket: category },
    );

  if (raw.parcels !== undefined && !Array.isArray(raw.parcels))
    fail("missing-data", "parcels must be an array", { unknown: true });
  for (const [index, parcel] of (Array.isArray(raw.parcels)
    ? raw.parcels
    : []
  ).entries()) {
    if (
      !object(parcel) ||
      typeof parcel.price !== "number" ||
      !Number.isFinite(parcel.price) ||
      parcel.price < 0
    ) {
      fail("invalid-amount", `parcel ${index + 1} needs a non-negative price`, {
        unknown: true,
      });
      continue;
    }
    const deposit = parcel.deposit ?? 0;
    const id =
      typeof parcel.id === "string" ? parcel.id : `parcel-${index + 1}`;
    if (
      typeof deposit !== "number" ||
      !Number.isFinite(deposit) ||
      deposit < 0 ||
      deposit > parcel.price
    ) {
      fail(
        "invalid-amount",
        `parcel ${id} deposit must be between zero and price`,
        { unknown: true },
      );
      continue;
    }
    if (deposit) {
      if (!parcel.exchange)
        fail("missing-data", `parcel ${id} needs exchange date for deposit`, {
          unknown: true,
        });
      else
        post(
          parcel.exchange,
          {
            category: "parcel-deposit",
            amount: -deposit,
            project: -deposit,
            operator: 0,
            itemId: id,
            description: `Parcel ${id} deposit`,
          },
          { funding: "acquisition", bucket: "acquisition" },
        );
    }
    if (parcel.price - deposit) {
      if (!parcel.settlement)
        fail("missing-data", `parcel ${id} needs settlement date`, {
          unknown: true,
        });
      else {
        const amount = parcel.price - deposit;
        post(
          parcel.settlement,
          {
            category: "parcel-settlement",
            amount: -amount,
            project: -amount,
            operator: 0,
            itemId: id,
            description: `Parcel ${id} settlement`,
          },
          { funding: "acquisition", bucket: "acquisition" },
        );
      }
    }
  }
  if (raw.costs !== undefined && !Array.isArray(raw.costs))
    fail("missing-data", "costs must be an array", { unknown: true });
  for (const [index, cost] of (Array.isArray(raw.costs)
    ? raw.costs
    : []
  ).entries()) {
    if (!object(cost) || typeof cost.category !== "string" || !cost.category) {
      fail("missing-data", `cost ${index + 1} needs category`, {
        unknown: true,
      });
      continue;
    }
    const id = typeof cost.id === "string" ? cost.id : `cost-${index + 1}`;
    if (cost.recurring !== undefined) {
      if (
        !object(cost.recurring) ||
        typeof cost.recurring.amount !== "number" ||
        !Number.isFinite(cost.recurring.amount) ||
        cost.recurring.amount < 0
      ) {
        fail(
          "invalid-amount",
          `recurring cost ${id} needs a non-negative amount`,
          { unknown: true },
        );
        continue;
      }
      const first = date(cost.recurring.start),
        last = date(cost.recurring.end),
        frequencyRaw = cost.recurring.frequency ?? "monthly";
      if (
        !first ||
        !last ||
        first > last ||
        typeof frequencyRaw !== "string" ||
        !["daily", "monthly", "annual"].includes(frequencyRaw)
      ) {
        fail(
          "invalid-range",
          `recurring cost ${id} has invalid dates or frequency`,
          { unknown: true },
        );
        continue;
      }
      const frequency = frequencyRaw as "daily" | "monthly" | "annual";
      for (
        let occurrence = first;
        occurrence <= last;
        occurrence = nextOccurrence(occurrence, frequency)
      )
        postCost(iso(occurrence), cost.recurring.amount, cost.category, id);
      continue;
    }
    if (
      typeof cost.amount !== "number" ||
      !Number.isFinite(cost.amount) ||
      cost.amount < 0
    ) {
      fail("invalid-amount", `cost ${id} needs non-negative amount`, {
        unknown: true,
      });
      continue;
    }
    if (cost.profile !== undefined) {
      if (!object(cost.profile) || !Object.keys(cost.profile).length) {
        fail("missing-data", `cost ${id} has an empty profile`, {
          unknown: true,
        });
        continue;
      }
      const profile = Object.entries(cost.profile);
      if (
        profile.some(
          ([, weight]) =>
            typeof weight !== "number" ||
            !Number.isFinite(weight) ||
            weight < 0,
        ) ||
        Math.abs(
          profile.reduce((total, [, weight]) => total + (weight as number), 0) -
            1,
        ) > 1e-8
      ) {
        fail(
          "invalid-amount",
          `cost ${id} profile weights must be finite, non-negative and sum to one`,
          { unknown: true },
        );
        continue;
      }
      let remainder = cent(cost.amount);
      for (const [position, [key, weight]] of profile.entries()) {
        const when = resolve(key, `cost ${id} profile`);
        if (!when) continue;
        const amount =
          position === profile.length - 1
            ? remainder
            : cent(cost.amount * (weight as number));
        remainder = cent(remainder - amount);
        postCost(when, amount, cost.category, id);
      }
      continue;
    }
    const when = cost.date
      ? resolve(cost.date, `cost ${id}`)
      : cost.milestone
        ? resolve(cost.milestone, `cost ${id}`)
        : null;
    if (!when)
      fail(
        "missing-data",
        `cost ${id} needs date, milestone, profile, or recurring schedule`,
        { unknown: true },
      );
    else postCost(when, cost.amount, cost.category, id);
  }
  const boundary = (value: unknown): Date | null => {
    const direct = date(value);
    return (
      direct ??
      (typeof value === "string" ? (milestoneDates.get(value) ?? null) : null)
    );
  };
  const opening =
    raw.openingDate === undefined ? null : boundary(raw.openingDate);
  const rentStart =
    raw.rentCommencementDate === undefined
      ? null
      : boundary(raw.rentCommencementDate);
  if (raw.openingDate !== undefined && !opening)
    fail("invalid-date", "openingDate is invalid", { unknown: true });
  if (raw.rentCommencementDate !== undefined && !rentStart)
    fail("invalid-date", "rentCommencementDate is invalid", { unknown: true });
  const postLines = (
    rawLines: unknown,
    type:
      | "landlord-rent"
      | "landlord-expense"
      | "operator-revenue"
      | "operator-expense",
  ): void => {
    if (rawLines === undefined) return;
    if (!Array.isArray(rawLines)) {
      fail("missing-data", `${type} must be an array`, { unknown: true });
      return;
    }
    for (const [index, line] of rawLines.entries()) {
      if (
        !object(line) ||
        typeof line.amount !== "number" ||
        !Number.isFinite(line.amount) ||
        line.amount < 0
      ) {
        fail(
          "invalid-amount",
          `${type} line ${index + 1} needs non-negative amount`,
          { unknown: true },
        );
        continue;
      }
      const reference = line.milestone ?? line.date;
      let when = boundary(reference);
      const offset = line.days as unknown;
      if (
        !when ||
        (offset !== undefined &&
          (typeof offset !== "number" ||
            !Number.isInteger(offset) ||
            offset < 0))
      ) {
        fail(
          "invalid-date",
          `${type} line ${index + 1} invalid date or milestone`,
          { unknown: true },
        );
        continue;
      }
      if (typeof offset === "number" && offset) when = plusDays(when, offset);
      if (
        (type === "operator-revenue" && opening && when < opening) ||
        (type === "landlord-rent" && rentStart && when < rentStart)
      )
        continue;
      const description =
        typeof line.category === "string" ? line.category : type;
      if (type === "landlord-rent")
        post(iso(when), {
          category: type,
          amount: line.amount,
          project: line.amount,
          operator: -line.amount,
          description,
        });
      if (type === "landlord-expense")
        post(
          iso(when),
          {
            category: type,
            amount: -line.amount,
            project: -line.amount,
            operator: 0,
            description,
          },
          { funding: description },
        );
      if (type === "operator-revenue")
        post(iso(when), {
          category: type,
          amount: line.amount,
          project: 0,
          operator: line.amount,
          description,
        });
      if (type === "operator-expense")
        post(iso(when), {
          category: type,
          amount: -line.amount,
          project: 0,
          operator: -line.amount,
          description,
        });
    }
  };
  postLines(raw.landlordRent, "landlord-rent");
  postLines(raw.landlordExpenses, "landlord-expense");
  postLines(raw.operatorRevenue, "operator-revenue");
  postLines(raw.operatorExpenses, "operator-expense");
  if (raw.contributions !== undefined && !Array.isArray(raw.contributions))
    fail("missing-data", "contributions must be an array", { unknown: true });
  for (const [index, contribution] of (Array.isArray(raw.contributions)
    ? (raw.contributions as Contribution[])
    : []
  ).entries()) {
    if (
      !object(contribution) ||
      typeof contribution.amount !== "number" ||
      !Number.isFinite(contribution.amount) ||
      contribution.amount < 0
    ) {
      fail(
        "invalid-amount",
        `contribution ${index + 1} needs non-negative amount`,
        { unknown: true },
      );
      continue;
    }
    if (
      !contribution.party ||
      !contribution.source ||
      !contribution.specifiedPurpose
    )
      fail(
        "missing-data",
        `contribution ${index + 1} needs party, source, and specifiedPurpose`,
        { unknown: true },
      );
    if (
      !contribution.purpose ||
      !["funding", "rent-credit", "tenant-owned-work"].includes(
        contribution.purpose,
      )
    ) {
      fail(
        "missing-data",
        `contribution ${index + 1} needs recognised purpose`,
        { unknown: true },
      );
      continue;
    }
    const description =
      contribution.specifiedPurpose || `${contribution.purpose} contribution`;
    if (contribution.purpose === "funding")
      post(contribution.date, {
        category: "contribution",
        amount: contribution.amount,
        project: contribution.amount,
        operator: -contribution.amount,
        description,
      });
    if (contribution.purpose === "rent-credit")
      post(contribution.date, {
        category: "contribution",
        amount: -contribution.amount,
        project: -contribution.amount,
        operator: contribution.amount,
        description,
      });
    if (contribution.purpose === "tenant-owned-work")
      post(contribution.date, {
        category: "contribution",
        amount: -contribution.amount,
        project: 0,
        operator: -contribution.amount,
        description,
      });
  }
  if (raw.terminalProceeds !== undefined) {
    if (
      !object(raw.terminalProceeds) ||
      typeof raw.terminalProceeds.amount !== "number" ||
      !Number.isFinite(raw.terminalProceeds.amount) ||
      raw.terminalProceeds.amount < 0
    )
      fail("invalid-amount", "terminal proceeds needs non-negative amount", {
        unknown: true,
      });
    else {
      const terminalDate = date(raw.terminalProceeds.date);
      if (terminalDate && inRange(terminalDate))
        terminalDates.add(iso(terminalDate));
      post(raw.terminalProceeds.date, {
        category: "terminal-proceeds",
        amount: raw.terminalProceeds.amount,
        project: raw.terminalProceeds.amount,
        operator: 0,
        description:
          typeof raw.terminalProceeds.description === "string"
            ? raw.terminalProceeds.description
            : "Terminal proceeds",
      });
    }
  }

  if (raw.facilities !== undefined && !Array.isArray(raw.facilities))
    fail("missing-data", "facilities must be an array", { unknown: true });
  const facilities = Array.isArray(raw.facilities)
    ? (raw.facilities as Facility[])
    : [];
  let equityBudget = Infinity;
  if (
    facilities.length &&
    (typeof raw.equityBudget !== "number" ||
      !Number.isFinite(raw.equityBudget) ||
      raw.equityBudget < 0)
  ) {
    fail(
      "missing-data",
      "facility scenarios require explicit non-negative equityBudget",
      { unknown: true },
    );
    equityBudget = 0;
  } else if (
    typeof raw.equityBudget === "number" &&
    Number.isFinite(raw.equityBudget) &&
    raw.equityBudget >= 0
  )
    equityBudget = raw.equityBudget;
  else if (raw.equityBudget !== undefined)
    fail("invalid-amount", "equityBudget must be non-negative finite number", {
      unknown: true,
    });
  const states = new Map<string, FacilityState>();
  for (const [index, facility] of facilities.entries()) {
    if (
      !object(facility) ||
      typeof facility.id !== "string" ||
      !facility.id ||
      states.has(facility.id)
    ) {
      fail("missing-data", `facility ${index + 1} needs unique id`, {
        unknown: true,
      });
      continue;
    }
    const startDate = date(
      facility.availability?.start ??
        facility.availabilityStart ??
        facility.open,
    );
    const availabilityEnd = date(
      facility.availability?.end ?? facility.availabilityEnd ?? facility.close,
    );
    const maturity = date(
      facility.maturityDate ??
        facility.availability?.end ??
        facility.availabilityEnd ??
        facility.close,
    );
    let valid = true;
    if (
      !startDate ||
      !availabilityEnd ||
      !maturity ||
      startDate > availabilityEnd ||
      availabilityEnd > maturity
    ) {
      fail(
        "invalid-range",
        `facility ${facility.id} needs ordered availability and maturity dates`,
        { itemId: facility.id, unknown: true },
      );
      valid = false;
    }
    if (
      typeof facility.limit !== "number" ||
      !Number.isFinite(facility.limit) ||
      facility.limit < 0 ||
      typeof facility.annualRate !== "number" ||
      !Number.isFinite(facility.annualRate) ||
      facility.annualRate < 0
    ) {
      fail(
        "invalid-amount",
        `facility ${facility.id} has invalid limit or annualRate`,
        { itemId: facility.id, unknown: true },
      );
      valid = false;
    }
    if (
      !Array.isArray(facility.eligibleCategories) ||
      !facility.eligibleCategories.length
    ) {
      fail("missing-data", `facility ${facility.id} needs eligibleCategories`, {
        itemId: facility.id,
        unknown: true,
      });
      valid = false;
    }
    if (
      facility.equityMode !== "equity-first" &&
      facility.equityMode !== "pro-rata"
    ) {
      fail("missing-data", `facility ${facility.id} needs equityMode`, {
        itemId: facility.id,
        unknown: true,
      });
      valid = false;
    }
    if (
      facility.equityMode === "pro-rata" &&
      (typeof facility.fundingShare !== "number" ||
        !Number.isFinite(facility.fundingShare) ||
        facility.fundingShare < 0 ||
        facility.fundingShare > 1)
    ) {
      fail(
        "missing-data",
        `pro-rata facility ${facility.id} needs fundingShare 0..1`,
        { itemId: facility.id, unknown: true },
      );
      valid = false;
    }
    if (!valid) continue;
    states.set(facility.id, {
      f: facility,
      start: startDate as Date,
      availabilityEnd: availabilityEnd as Date,
      maturity: maturity as Date,
      debt: 0,
      interestAccrued: 0,
      feeAccrued: 0,
    });
    if (inRange(startDate as Date)) event(iso(startDate as Date));
    if (inRange(availabilityEnd as Date)) event(iso(availabilityEnd as Date));
    if (inRange(maturity as Date)) event(iso(maturity as Date));
    const fee = facility.establishmentFee;
    const upfront =
      typeof fee === "number"
        ? fee
        : (fee?.fixed ?? 0) + facility.limit * (fee?.percent ?? 0);
    if (!Number.isFinite(upfront) || upfront < 0)
      fail(
        "invalid-amount",
        `facility ${facility.id} establishmentFee is invalid`,
        { itemId: facility.id, unknown: true },
      );
    else if (upfront && inRange(startDate as Date)) {
      event(iso(startDate as Date)).flow.fees = cent(
        event(iso(startDate as Date)).flow.fees + upfront,
      );
      ledger.push({
        date: iso(startDate as Date),
        category: "facility-fee",
        amount: -upfront,
        project: 0,
        operator: 0,
        facilityId: facility.id,
        description: `Facility ${facility.id} establishment fee`,
      });
    }
    for (const [basis, rate] of Object.entries(facility.recurringFees ?? {}))
      if (
        typeof rate !== "number" ||
        !Number.isFinite(rate) ||
        rate < 0 ||
        !["limit", "drawn", "undrawn"].includes(basis)
      )
        fail(
          "invalid-amount",
          `facility ${facility.id} recurring fee ${basis} is invalid`,
          { itemId: facility.id, unknown: true },
        );
  }
  if (start && finish) {
    for (
      let d = endOfMonth(start);
      d <= finish;
      d = endOfMonth(
        new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1)),
      )
    )
      event(iso(d));
    event(iso(finish));
  }
  const refinanceByDate = new Map<string, Array<Record<string, unknown>>>();
  if (raw.refinance !== undefined && !Array.isArray(raw.refinance))
    fail("missing-data", "refinance must be an array", { unknown: true });
  for (const [index, record] of (Array.isArray(raw.refinance)
    ? raw.refinance
    : []
  ).entries()) {
    if (!object(record) || !date(record.date)) {
      fail("invalid-date", `refinance ${index + 1} has invalid date`, {
        unknown: true,
      });
      continue;
    }
    const d = date(record.date) as Date;
    if (!inRange(d)) {
      fail("invalid-range", `refinance ${index + 1} outside analysis period`, {
        unknown: true,
      });
      continue;
    }
    const key = iso(d);
    const rows = refinanceByDate.get(key) ?? [];
    rows.push(record);
    refinanceByDate.set(key, rows);
    event(key);
  }

  const dates = [...events.keys()].sort();
  let previous = start;
  let equityUsed = 0;
  let peakDebt = 0;
  const facilityLine = (
    when: string,
    category: "facility-interest" | "facility-fee",
    amount: number,
    state: FacilityState,
  ): void => {
    ledger.push({
      date: when,
      category,
      amount: -amount,
      project: 0,
      operator: 0,
      facilityId: state.f.id,
      description: `Facility ${state.f.id} ${category === "facility-interest" ? "interest" : "fee"}`,
    });
  };
  const capitalise = (
    when: string,
    amount: number,
    state: FacilityState,
    flow: DatedCashflow,
    what: string,
  ): void => {
    const draw = cent(
      Math.min(amount, Math.max(0, state.f.limit - state.debt)),
    );
    if (draw) {
      state.debt = cent(state.debt + draw);
      flow.loanDraws = cent(flow.loanDraws + draw);
      flow.capitalisedDraws = cent(flow.capitalisedDraws + draw);
      ledger.push({
        date: when,
        category: "facility-draw",
        amount: draw,
        project: 0,
        operator: 0,
        facilityId: state.f.id,
        description: `Capitalised ${what}`,
      });
    }
    if (draw < amount) {
      flow.fundingShortfall = cent(flow.fundingShortfall + amount - draw);
      fail(
        "capacity-shortfall",
        `${state.f.id} lacks capacity for capitalised ${what}`,
        { date: when, itemId: state.f.id, unknown: true },
      );
    }
  };
  for (const key of dates) {
    const current = date(key) as Date;
    const { flow, categoryAmounts } = events.get(key) as Event;
    const days =
      previous && current >= previous
        ? (current.getTime() - previous.getTime()) / DAY
        : 0;
    if (days)
      for (const state of states.values()) {
        const interestStart =
          previous && previous > state.start ? previous : state.start;
        const interestEnd = current < state.maturity ? current : state.maturity;
        const interestDays = Math.max(
          0,
          (interestEnd.getTime() - interestStart.getTime()) / DAY,
        );
        if (state.debt && interestDays)
          state.interestAccrued +=
            (state.debt * state.f.annualRate * interestDays) / 365;
        const feeStart =
          previous && previous > state.start ? previous : state.start;
        const feeEnd =
          current < state.availabilityEnd ? current : state.availabilityEnd;
        const feeDays = Math.max(
          0,
          (feeEnd.getTime() - feeStart.getTime()) / DAY,
        );
        const fees = state.f.recurringFees;
        if (fees && feeDays)
          state.feeAccrued +=
            ((state.f.limit * (fees.limit ?? 0) +
              state.debt * (fees.drawn ?? 0) +
              Math.max(0, state.f.limit - state.debt) * (fees.undrawn ?? 0)) *
              feeDays) /
            365;
      }
    const disposition = terminalDates.has(key);
    const finalCharge =
      Boolean(finish && current.getTime() === finish.getTime()) ||
      disposition ||
      [...states.values()].some(
        (s) => s.maturity.getTime() === current.getTime(),
      );
    const charge =
      isMonthEnd(current) || finalCharge || refinanceByDate.has(key);
    // Monthly invoices truncate to cents and retain fractional carry; the final settlement rounds
    // the whole remaining ACT/365 accrual, avoiding arbitrary cumulative invoice-rounding drift.
    const chargeAmount = (accrued: number): number =>
      finalCharge
        ? cent(accrued)
        : Math.floor((accrued + Number.EPSILON) * 100) / 100;
    if (charge)
      for (const state of states.values()) {
        if (state.interestAccrued > 0.000001) {
          const amount = chargeAmount(state.interestAccrued);
          state.interestAccrued -= amount;
          flow.interest = cent(flow.interest + amount);
          facilityLine(key, "facility-interest", amount, state);
          if ((state.f.interest ?? "paid") === "capitalised") {
            flow.capitalisedInterest = cent(flow.capitalisedInterest + amount);
            capitalise(key, amount, state, flow, "interest");
          } else flow.paidInterest = cent(flow.paidInterest + amount);
        }
        if (state.feeAccrued > 0.000001) {
          const amount = chargeAmount(state.feeAccrued);
          state.feeAccrued -= amount;
          flow.fees = cent(flow.fees + amount);
          facilityLine(key, "facility-fee", amount, state);
        }
      }
    for (const record of refinanceByDate.get(key) ?? []) {
      const from =
        typeof record.fromFacility === "string"
          ? states.get(record.fromFacility)
          : undefined;
      const to =
        typeof record.toFacility === "string"
          ? states.get(record.toFacility)
          : undefined;
      const wanted =
        record.principal === undefined ? from?.debt : record.principal;
      if (
        !from ||
        !to ||
        from === to ||
        current < to.start ||
        current > to.availabilityEnd ||
        typeof wanted !== "number" ||
        !Number.isFinite(wanted) ||
        wanted <= 0
      ) {
        fail(
          "missing-data",
          `refinance on ${key} needs distinct available facilities and positive principal`,
          { date: key, unknown: true },
        );
        continue;
      }
      const moved = cent(
        Math.min(wanted, from.debt, Math.max(0, to.f.limit - to.debt)),
      );
      if (moved < wanted) {
        flow.fundingShortfall = cent(flow.fundingShortfall + wanted - moved);
        fail(
          "capacity-shortfall",
          `refinance on ${key} cannot transfer requested principal`,
          { date: key, unknown: true },
        );
      }
      if (moved) {
        from.debt = cent(from.debt - moved);
        to.debt = cent(to.debt + moved);
        flow.loanRepayments = cent(flow.loanRepayments + moved);
        flow.loanDraws = cent(flow.loanDraws + moved);
        flow.refinanceRepayments = cent(flow.refinanceRepayments + moved);
        flow.refinanceDraws = cent(flow.refinanceDraws + moved);
        ledger.push(
          {
            date: key,
            category: "refinance-transfer",
            amount: -moved,
            project: 0,
            operator: 0,
            facilityId: from.f.id,
            description: `Refinance transfer to ${to.f.id}`,
          },
          {
            date: key,
            category: "refinance-transfer",
            amount: moved,
            project: 0,
            operator: 0,
            facilityId: to.f.id,
            description: `Refinance transfer from ${from.f.id}`,
          },
        );
      }
    }
    for (const state of states.values())
      if (state.maturity.getTime() === current.getTime() && state.debt) {
        const repayment = state.debt;
        state.debt = 0;
        flow.loanRepayments = cent(flow.loanRepayments + repayment);
        ledger.push({
          date: key,
          category: "facility-repayment",
          amount: -repayment,
          project: 0,
          operator: 0,
          facilityId: state.f.id,
          description: `Facility ${state.f.id} maturity repayment`,
        });
      }
    if (disposition)
      for (const state of states.values())
        if (state.debt) {
          const repayment = state.debt;
          state.debt = 0;
          flow.loanRepayments = cent(flow.loanRepayments + repayment);
          ledger.push({
            date: key,
            category: "facility-repayment",
            amount: -repayment,
            project: 0,
            operator: 0,
            facilityId: state.f.id,
            description: `Facility ${state.f.id} terminal disposition repayment`,
          });
        }
    let provisionalEquity = Math.max(0, equityBudget - equityUsed);
    for (const [category, categoryCost] of categoryAmounts) {
      const eligible = [...states.values()].filter(
        (s) =>
          current >= s.start &&
          current < s.maturity &&
          current <= s.availabilityEnd &&
          (s.f.eligibleCategories?.includes(category) ||
            s.f.eligibleCategories?.includes("*")),
      );
      const modes = new Set(eligible.map((s) => s.f.equityMode));
      if (modes.size > 1) {
        fail(
          "missing-data",
          `eligible facilities use conflicting equity modes for ${category} on ${key}`,
          { date: key, unknown: true },
        );
        continue;
      }
      const mode = eligible[0]?.f.equityMode;
      const shares = eligible.reduce(
        (sum, s) => sum + (s.f.fundingShare ?? 0),
        0,
      );
      if (mode === "pro-rata" && shares > 1 + 0.00000001)
        fail(
          "invalid-range",
          `pro-rata shares exceed 100% for ${category} on ${key}`,
          { date: key, unknown: true },
        );
      const debtTarget =
        mode === "equity-first"
          ? Math.max(0, categoryCost - provisionalEquity)
          : mode === "pro-rata"
            ? cent(categoryCost * Math.min(1, shares))
            : 0;
      if (mode === "equity-first")
        provisionalEquity = Math.max(
          0,
          provisionalEquity - Math.min(categoryCost, provisionalEquity),
        );
      let remaining = debtTarget;
      for (const state of eligible) {
        const requested =
          mode === "pro-rata" && shares
            ? cent((debtTarget * (state.f.fundingShare ?? 0)) / shares)
            : remaining;
        const draw = cent(
          Math.min(
            requested,
            remaining,
            Math.max(0, state.f.limit - state.debt),
          ),
        );
        if (draw) {
          state.debt = cent(state.debt + draw);
          flow.loanDraws = cent(flow.loanDraws + draw);
          ledger.push({
            date: key,
            category: "facility-draw",
            amount: draw,
            project: 0,
            operator: 0,
            facilityId: state.f.id,
            description: `Facility ${state.f.id} ${category} draw`,
          });
        }
        remaining = cent(Math.max(0, remaining - draw));
      }
    }
    const paidFinance = cent(flow.fees + flow.paidInterest);
    const cashDraws = cent(
      flow.loanDraws - flow.capitalisedDraws - flow.refinanceDraws,
    );
    const cashRepayments = cent(flow.loanRepayments - flow.refinanceRepayments);
    const beforeEquity = cent(
      flow.project + cashDraws - cashRepayments - paidFinance,
    );
    if (beforeEquity < 0) {
      const required = -beforeEquity;
      const supplied = cent(
        Math.min(required, Math.max(0, equityBudget - equityUsed)),
      );
      flow.equity = -supplied;
      equityUsed = cent(equityUsed + supplied);
      if (supplied < required) {
        flow.fundingShortfall = cent(
          flow.fundingShortfall + required - supplied,
        );
        fail(
          "capacity-shortfall",
          `developer equity budget is insufficient on ${key}`,
          { date: key, unknown: true },
        );
      }
    } else flow.equity = beforeEquity;
    flow.closingDebt = cent(
      [...states.values()].reduce((sum, s) => sum + s.debt, 0),
    );
    peakDebt = Math.max(peakDebt, flow.closingDebt);
    previous = current;
  }
  const cashflows = dates.map((d) => (events.get(d) as Event).flow);
  const monthly = new Map<string, DatedMonthlyRollup>();
  for (const flow of cashflows) {
    const month = flow.date.slice(0, 7);
    const current = monthly.get(month) ?? { ...blank(flow.date), month };
    for (const key of [
      "project",
      "equity",
      "operator",
      "loanDraws",
      "loanRepayments",
      "interest",
      "paidInterest",
      "capitalisedInterest",
      "fees",
      "capitalisedDraws",
      "refinanceDraws",
      "refinanceRepayments",
      "fundingShortfall",
    ] as const)
      current[key] = cent(current[key] + flow[key]);
    current.closingDebt = flow.closingDebt;
    monthly.set(month, current);
  }
  const totals = cashflows.reduce(
    (total, flow) => {
      for (const key of [
        "project",
        "equity",
        "operator",
        "loanDraws",
        "loanRepayments",
        "interest",
        "fees",
        "fundingShortfall",
      ] as const)
        total[key] = cent(total[key] + flow[key]);
      return total;
    },
    {
      project: 0,
      equity: 0,
      operator: 0,
      loanDraws: 0,
      loanRepayments: 0,
      interest: 0,
      fees: 0,
      fundingShortfall: 0,
    },
  );
  const projectValues = cashflows.map((flow) => ({
    date: date(flow.date) as Date,
    value: flow.project,
  }));
  const equityValues = cashflows.map((flow) => ({
    date: date(flow.date) as Date,
    value: flow.equity,
  }));
  const complete = issues.length === 0;
  const residualDebt = cashflows.at(-1)?.closingDebt ?? 0;
  const returnsSettled = complete && residualDebt === 0;
  const returnWarnings = returnsSettled
    ? []
    : residualDebt > 0
      ? [
          `Outstanding debt of ${residualDebt.toFixed(2)} remains at analysis end; IRR is not a settled investment return.`,
        ]
      : [];
  return {
    complete,
    returnsSettled,
    returnWarnings,
    issues,
    unresolvedInputs: [...new Set(unresolved)],
    resolvedMilestones: Object.fromEntries(
      [...milestoneDates.entries()].map(([id, value]) => [id, iso(value)]),
    ),
    cashflows,
    ledger: ledger.sort((a, b) => a.date.localeCompare(b.date)),
    monthly: [...monthly.values()],
    projectIRR:
      returnsSettled && signs(projectValues) === 1 ? xirr(projectValues) : null,
    equityIRR:
      returnsSettled && signs(equityValues) === 1 ? xirr(equityValues) : null,
    projectNPV: complete
      ? xnpv(projectValues, discountRate, start ?? undefined)
      : null,
    equityNPV: complete
      ? xnpv(equityValues, discountRate, start ?? undefined)
      : null,
    discountRate,
    totals,
    costBuckets,
    peakDebt: cent(peakDebt),
    equityRequired: cent(equityUsed),
  };
}
